import { useState } from 'preact/hooks';
import { api } from '../api.ts';
import { bump } from '../signal.ts';
import { show, showError } from '../toast.ts';
import { MEAL_LABEL } from '../format.ts';
import { MEALS, type Entry, type Meal } from '../../shared/types.ts';
import { Sheet } from './Sheet.tsx';

const toNum = (s: string) => Number(s.replace(',', '.'));

export function EntrySheet({ entry, onClose }: { entry: Entry; onClose: () => void }) {
  const [meal, setMeal] = useState<Meal>(entry.meal);
  const [name, setName] = useState(entry.name);
  const [servings, setServings] = useState(entry.servings === null ? '' : String(+entry.servings.toFixed(2)));
  const [m, setM] = useState({
    kcal: String(Math.round(entry.kcal)),
    protein: String(entry.protein),
    carbs: String(entry.carbs),
    fat: String(entry.fat),
  });
  const fromFood = entry.foodId !== null && entry.servings !== null;
  const servingsChanged = fromFood && toNum(servings) !== entry.servings;

  async function save(e: Event) {
    e.preventDefault();
    const body: Record<string, unknown> = { meal, name: name.trim() || entry.name };
    if (servingsChanged) {
      if (!(toNum(servings) > 0)) return showError('Servings must be more than 0');
      body.servings = toNum(servings);
    } else {
      for (const k of ['kcal', 'protein', 'carbs', 'fat'] as const) {
        const v = toNum(m[k]);
        if (!(v >= 0)) return showError(`${k} must be a number`);
        body[k] = v;
      }
    }
    try {
      await api(`/api/entries/${entry.id}`, { method: 'PATCH', body });
      bump();
      onClose();
    } catch (err) {
      showError(err);
    }
  }

  async function remove() {
    try {
      await api('/api/entries/delete', { body: { ids: [entry.id] } });
      bump();
      onClose();
      show(`Deleted ${entry.name}`, {
        action: { label: 'Undo', run: () => api('/api/entries/restore', { body: { ids: [entry.id] } }).then(bump, showError) },
      });
    } catch (err) {
      showError(err);
    }
  }

  return (
    <Sheet title="Edit entry" onClose={onClose}>
      <form class="form" onSubmit={save}>
        <label><span>Name</span><input value={name} onInput={(e) => setName(e.currentTarget.value)} /></label>
        <p class="muted">{entry.amount}{entry.source === 'ai' ? ' · estimated by AI' : ''}</p>
        {fromFood && (
          <label>
            <span>Servings</span>
            <input inputMode="decimal" value={servings} onInput={(e) => setServings(e.currentTarget.value)} />
          </label>
        )}
        <div class="macroinputs">
          {(['kcal', 'protein', 'carbs', 'fat'] as const).map((k) => (
            <label key={k}>
              <span>{k === 'kcal' ? 'Calories' : `${k[0].toUpperCase()}${k.slice(1)} (g)`}</span>
              <input
                inputMode="decimal"
                value={m[k]}
                disabled={servingsChanged}
                onInput={(e) => setM((o) => ({ ...o, [k]: e.currentTarget.value }))}
              />
            </label>
          ))}
        </div>
        <div class="segmented">
          {MEALS.map((x) => (
            <button type="button" key={x} class={meal === x ? 'on' : ''} onClick={() => setMeal(x)}>{MEAL_LABEL[x]}</button>
          ))}
        </div>
        <div class="row">
          <button type="button" class="danger" onClick={remove}>Delete</button>
          <button class="primary">Save</button>
        </div>
      </form>
    </Sheet>
  );
}
